import { useSyncExternalStore } from 'react';

// ============================================================================
// sizeClass.ts (E1/Paket K) — die JS-Spiegelung der Breakpoint-Leiter aus
// docs/30 §5. CSS entscheidet, JS liest nur mit: diese Klassen duerfen NUR
// Verhalten, ARIA und Datenmengen (React-Query `enabled:`) steuern — NIEMALS
// ueber conditional Rendering ganzer Teilbaeume (remountet beim Live-Resize).
// ----------------------------------------------------------------------------
// Drei orthogonale Achsen:
//   • Breite  → SizeClass  (matchMedia, Fensterbreite)
//   • Hoehe   → HeightClass (gegen die EFFEKTIVE Hoehe aus visualViewport,
//               NICHT matchMedia: der Layout-Viewport schrumpft unter iOS beim
//               Tastaturoeffnen nicht — die Hoehenklasse waere sonst gegen das
//               haeufigste Hoehenereignis der App blind, docs/30 §5)
//   • Zeiger  → PointerClass (Laufzeit-EVENT-Detektor, nicht `(pointer:fine)`:
//               iPad meldet mit Trackpad `pointer:coarse` und wechselt die Query
//               beim Eingabegeraetewechsel nicht, WebKit-Bug 209292)
//   • Lage    → Orientation (L1): `innerWidth > innerHeight`, NICHT
//               `(orientation: landscape)`. Die Media-Query kippt auf dem Handy,
//               sobald die Bildschirmtastatur den Layout-Viewport flacher macht
//               als er breit ist — der Split wuerde mitten im Tippen aufklappen.
//               Der Vergleich der beiden Fensterkanten ist dagegen stabil, weil
//               `innerHeight` (Layout-Viewport) bei offener Tastatur unter iOS
//               gar nicht und unter Android nur um die Tastaturhoehe schrumpft;
//               ein Hochkant-Handy bleibt dabei hochkant. Fuer die Hoehenklasse
//               gilt weiterhin die EFFEKTIVE Hoehe (visualViewport) — das ist die
//               andere Frage („wie viel sehe ich?", nicht „wie liegt das Geraet?").
// ============================================================================

export type SizeClass = 'compact' | 'medium' | 'expanded' | 'wide';
export type HeightClass = 'short' | 'tight' | 'regular' | 'tall';
export type PointerClass = 'coarse' | 'fine';
// L1: vierte Achse — Ausrichtung. Sie entscheidet (zusammen mit der Container-
// Breite) ueber den Zwei-Spalten-Split, siehe SplitView.tsx.
export type Orientation = 'landscape' | 'portrait';

// Dieselben Zahlen wie in src/styles/layout.css. Wer hier etwas aendert, aendert
// dort mit — sonst laufen CSS-Shell und JS-Verhalten auseinander.
export const BP = {
  medium: 600,
  expanded: 1000,
  wide: 1280,
  // Hoehensprossen (docs/30 §5): short <=519 · tight 520-767 · regular 768-899 · tall >=900
  short: 519,
  tight: 767,
  tall: 900,
} as const;

const SIZE_ORDER: SizeClass[] = ['compact', 'medium', 'expanded', 'wide'];

function windowWidth(): number {
  if (typeof window === 'undefined') return 0;
  return window.innerWidth;
}

// LAYOUT-Hoehe (nicht visualViewport): siehe Kopf — die Lage des Geraets darf
// sich beim Oeffnen der Tastatur nicht aendern.
function windowHeight(): number {
  if (typeof window === 'undefined') return 0;
  return window.innerHeight;
}

// Effektive Hoehe = das, was der Nutzer wirklich sieht. visualViewport.height
// schrumpft beim Tastaturoeffnen (iOS/Chrome) — genau darauf soll die Klasse
// reagieren. Fallback auf innerHeight fuer Alt-Browser/SSR.
function effectiveHeight(): number {
  if (typeof window === 'undefined') return 0;
  const vv = window.visualViewport;
  if (vv && typeof vv.height === 'number' && vv.height > 0) return vv.height;
  return window.innerHeight;
}

export function sizeClassForWidth(width: number): SizeClass {
  if (width >= BP.wide) return 'wide';
  if (width >= BP.expanded) return 'expanded';
  if (width >= BP.medium) return 'medium';
  return 'compact';
}

export function orientationFor(width: number, height: number): Orientation {
  return width > height ? 'landscape' : 'portrait';
}

export function heightClassForHeight(height: number): HeightClass {
  if (height >= BP.tall) return 'tall';
  if (height >= BP.tight + 1) return 'regular'; // >=768
  if (height >= BP.short + 1) return 'tight'; // 520-767
  return 'short'; // <=519
}

function currentPointerFromMedia(): PointerClass {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'coarse';
  try {
    return window.matchMedia('(any-pointer: fine)').matches ? 'fine' : 'coarse';
  } catch {
    return 'coarse';
  }
}

// ----------------------------------------------------------------------------
// Ein gemeinsamer externer Store fuer alle drei Achsen. useSyncExternalStore
// verlangt eine stabile Snapshot-Referenz, solange sich nichts aendert — sonst
// Endlos-Rerender. Deshalb wird der Snapshot gecacht und nur bei echter
// Aenderung neu erzeugt.
// ----------------------------------------------------------------------------

interface Snapshot {
  size: SizeClass;
  height: HeightClass;
  pointer: PointerClass;
  orient: Orientation;
}

let pointerOverride: PointerClass | null = null; // vom Laufzeit-Detektor gesetzt

function computeSnapshot(): Snapshot {
  return {
    size: sizeClassForWidth(windowWidth()),
    height: heightClassForHeight(effectiveHeight()),
    pointer: pointerOverride ?? currentPointerFromMedia(),
    orient: orientationFor(windowWidth(), windowHeight()),
  };
}

let cached: Snapshot = computeSnapshot();
const subscribers = new Set<() => void>();

function recompute(): void {
  const next = computeSnapshot();
  if (
    next.size !== cached.size
    || next.height !== cached.height
    || next.pointer !== cached.pointer
    || next.orient !== cached.orient
  ) {
    cached = next;
    for (const cb of subscribers) cb();
  }
}

// matchMedia-Abos fuer alle Leiter-Schwellen. Der Test-Stub (src/test/setup.ts)
// feuert bei setViewport() die change-Events dieser Queries — dadurch reagiert
// die Hoehenklasse im Test, obwohl der visualViewport-Stub keine resize-Events
// kennt. In der echten App decken window/visualViewport-resize die Faelle ab,
// die matchMedia nicht sieht (z.B. Tastatur ohne Breakpoint-Wechsel).
const MEDIA_QUERIES = [
  `(min-width: ${BP.medium}px)`,
  `(min-width: ${BP.expanded}px)`,
  `(min-width: ${BP.wide}px)`,
  `(min-height: ${BP.short + 1}px)`,
  `(min-height: ${BP.tight + 1}px)`,
  `(min-height: ${BP.tall}px)`,
  '(any-pointer: fine)',
  // L1: der Test-Stub feuert bei setViewport() die change-Events aller
  // abonnierten Queries; diese hier haelt die Lage im jsdom aktuell, auch wenn
  // weder resize noch orientationchange gestellt werden.
  '(orientation: landscape)',
];

let installed = false;
// Rueckbau-Schritte der Installation — nur der Test-Reset nutzt sie, damit ein
// frischer Store gegen einen frisch gesetzten matchMedia-Stub arbeiten kann.
const teardown: (() => void)[] = [];

function ensureInstalled(): void {
  if (installed || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  installed = true;
  for (const q of MEDIA_QUERIES) {
    try {
      const mql = window.matchMedia(q);
      mql.addEventListener('change', recompute);
      teardown.push(() => mql.removeEventListener('change', recompute));
    } catch {
      // aeltere Umgebung ohne addEventListener — dann traegt window.resize.
    }
  }
  window.addEventListener('resize', recompute);
  window.addEventListener('orientationchange', recompute);
  window.visualViewport?.addEventListener('resize', recompute);
  window.visualViewport?.addEventListener('scroll', recompute);
  teardown.push(() => {
    window.removeEventListener('resize', recompute);
    window.removeEventListener('orientationchange', recompute);
    window.visualViewport?.removeEventListener('resize', recompute);
    window.visualViewport?.removeEventListener('scroll', recompute);
  });

  // Zeiger-EVENT-Detektor (WebKit-Bug 209292): der erste echte Zeiger-Kontakt
  // korrigiert die statische any-pointer-Vermutung. Maus/Stift = fine, Finger =
  // coarse. Passiv, damit kein Scroll-Jank.
  const onPointer = (ev: PointerEvent) => {
    const next: PointerClass = ev.pointerType === 'touch' ? 'coarse' : 'fine';
    if (next !== pointerOverride) {
      pointerOverride = next;
      recompute();
    }
  };
  window.addEventListener('pointerdown', onPointer, { passive: true, capture: true });
  window.addEventListener('pointermove', onPointer, { passive: true, capture: true });
  teardown.push(() => {
    window.removeEventListener('pointerdown', onPointer, { capture: true });
    window.removeEventListener('pointermove', onPointer, { capture: true });
  });
}

function subscribe(cb: () => void): () => void {
  ensureInstalled();
  subscribers.add(cb);
  // Zwischen dem Modul-Import (erste Berechnung) und dem ersten Abo kann sich
  // das Fenster geaendert haben, ohne dass jemand zugehoert hat — dann zeigte
  // der Cache einen veralteten Stand. Einmal nachziehen; `recompute` tauscht den
  // Snapshot nur bei echter Aenderung und meldet ihn den Abonnenten
  // (useSyncExternalStore liest danach ohnehin neu).
  recompute();
  return () => {
    subscribers.delete(cb);
  };
}

const SERVER_SNAPSHOT: Snapshot = {
  size: 'compact',
  height: 'regular',
  pointer: 'coarse',
  orient: 'portrait',
};

function getSnapshot(): Snapshot {
  return cached;
}

function getServerSnapshot(): Snapshot {
  return SERVER_SNAPSHOT;
}

export function useSizeClass(): SizeClass {
  return useSyncExternalStore(subscribe, () => getSnapshot().size, () => getServerSnapshot().size);
}

export function useHeightClass(): HeightClass {
  return useSyncExternalStore(subscribe, () => getSnapshot().height, () => getServerSnapshot().height);
}

export function usePointerClass(): PointerClass {
  return useSyncExternalStore(subscribe, () => getSnapshot().pointer, () => getServerSnapshot().pointer);
}

/** Lage des Fensters — 'landscape' nur, wenn es echt breiter als hoch ist. */
export function useOrientation(): Orientation {
  return useSyncExternalStore(subscribe, () => getSnapshot().orient, () => getServerSnapshot().orient);
}

/** Nicht-reaktiver Lesezugriff (Erst-Render/Fallbacks ohne Abo). */
export function currentOrientation(): Orientation {
  return cached.orient;
}

/** true, wenn die aktuelle Groessenklasse mindestens `min` ist. */
export function atLeast(min: SizeClass): boolean {
  return SIZE_ORDER.indexOf(cached.size) >= SIZE_ORDER.indexOf(min);
}

/**
 * Schreibt html[data-size]/[data-h]/[data-pointer] und haelt sie aktuell.
 * Einmalig aus App.tsx aufrufen. Das Inline-Script in index.html setzt die
 * Attribute schon VOR dem React-Mount (kein Layout-Flash) — dieser Aufruf
 * uebernimmt danach die Pflege ueber Resize/Zeiger/Tastatur.
 */
export function installSizeClassAttributes(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const root = document.documentElement;
  const apply = () => {
    root.setAttribute('data-size', cached.size);
    root.setAttribute('data-h', cached.height);
    root.setAttribute('data-pointer', cached.pointer);
    // L1: CSS-Spiegel der Lage. SplitView.module.css bindet das Zwei-Spalten-
    // Layout daran; das Inline-Script in index.html setzt das Attribut schon
    // VOR dem React-Mount, damit der Split nicht nachtraeglich umklappt.
    root.setAttribute('data-orient', cached.orient);
  };
  // recompute() aktualisiert `cached`; apply() spiegelt es auf <html>.
  recompute();
  apply();
  const unsub = subscribe(apply);
  return () => {
    unsub();
  };
}

// Nur fuer Tests: internen Zustand zuruecksetzen, damit ein frischer Store
// gegen den zurueckgesetzten Viewport-Stub arbeitet.
//
// WICHTIG (L1-Fix): `resetViewport()` im Test-Setup LEERT die Liste der lebenden
// MediaQueryList-Objekte des Stubs. Eine einmal installierte Modul-Installation
// waere danach taub — setViewport() erreicht sie nie wieder, der Store liefert
// bis zum Prozessende veraltete Werte (aufgefallen an der Lage: das Fenster war
// hochkant, der Store meldete weiter 'landscape'). Deshalb wird die Installation
// hier vollstaendig zurueckgebaut; das naechste Abo installiert frisch.
export function __resetSizeClassForTest(): void {
  for (const undo of teardown.splice(0)) {
    try {
      undo();
    } catch {
      /* Stub ohne removeEventListener — dann reicht der Neuaufbau */
    }
  }
  installed = false;
  pointerOverride = null;
  cached = computeSnapshot();
}
